import { useEffect, useMemo, useRef, useState } from "react";
import { BatteryCharging, BedDouble, BookOpen, MapPin, X, Zap, Clock, Route, Plus, ImagePlus, AlertTriangle, Navigation, RefreshCw, Sparkles, CalendarDays, Pencil, Trash2, Check, Loader2, Lock, Calculator } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API_BASE, thumbUrl } from "../lib/api";
import { confirmDialog } from "./ConfirmHost";
import { compressImage } from "../lib/imageCompress";
import { getPositionIfGranted, isTripToday, haversineKm } from "../lib/geo";
import { gmapsUrl, stayLinks } from "./Timeline";
import { LiveMap } from "./LiveMap";
import { SocChart } from "./SocChart";
import { LiveProgress } from "./LiveProgress";
import { ChargeTimer } from "./ChargeTimer";
import { VoiceRecorder } from "./VoiceRecorder";
import { AssistantChat } from "./AssistantChat";
import { Lightbox } from "./Lightbox";
import { ChargeCalcDialog } from "./ChargeCalculator";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Textarea } from "./ui/textarea";

const LiveLogEntry = ({ trip, e, onRefresh }) => {
  const [editing, setEditing] = useState(false);
  const [lb, setLb] = useState(null);
  const [draft, setDraft] = useState(e.text || "");
  const [draftTs, setDraftTs] = useState("");
  const [saving, setSaving] = useState(false);
  const toLocalInput = (iso) => {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const mediaUrl = (id) => `${API_BASE}/trips/${trip.id}/photos/${id}`;
  const images = (e.media || []).filter((m) => m.content_type?.startsWith("image"));
  const videos = (e.media || []).filter((m) => m.content_type?.startsWith("video"));
  const lbIds = [...(e.photo_id ? [e.photo_id] : []), ...images.map((m) => m.id)];
  const openLb = (id) => setLb({ urls: lbIds.map(mediaUrl), index: Math.max(0, lbIds.indexOf(id)) });
  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.patch(`/trips/${trip.id}/log/${e.id}`, { text: draft.trim(), timestamp: draftTs ? new Date(draftTs).toISOString() : null });
      setEditing(false);
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  const removePhoto = async (pid) => {
    if (!(await confirmDialog("Deze foto/video verwijderen uit dit moment?", { title: "Media verwijderen", confirmLabel: "Verwijderen", destructive: true }))) return;
    try {
      await api.delete(`/trips/${trip.id}/log/${e.id}/photos/${pid}`);
      toast.success("Foto verwijderd uit dit moment");
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const RemoveBtn = ({ id }) => (
    <button data-testid={`live-remove-photo-${id}`} type="button" onClick={() => removePhoto(id)} title="Verwijder deze foto/video uit dit moment"
      className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/60 hover:bg-red-600 text-white flex items-center justify-center transition-[background-color] duration-200">
      <X className="w-4 h-4" />
    </button>
  );
  return (
    <div className="bg-card rounded-2xl p-4" data-testid={`live-log-entry-${e.id}`}>
      {lb && <Lightbox urls={lb.urls} index={lb.index} onClose={() => setLb(null)} />}
      {e.photo_id && (
        <div className="relative">
          <img src={thumbUrl(mediaUrl(e.photo_id))} alt="Reislog foto" loading="lazy" decoding="async" onClick={() => openLb(e.photo_id)} className="rounded-xl mb-3 w-full max-h-56 object-cover cursor-zoom-in" />
          {editing && <RemoveBtn id={e.photo_id} />}
        </div>
      )}
      {images.length > 0 && (
        <div className={`grid gap-2 mb-3 ${images.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
          {images.map((m) => (
            <div key={m.id} className="relative">
              <img src={thumbUrl(mediaUrl(m.id))} alt="Reisfoto" loading="lazy" decoding="async" onClick={() => openLb(m.id)} className="rounded-xl w-full max-h-56 object-cover cursor-zoom-in" />
              {editing && <RemoveBtn id={m.id} />}
            </div>
          ))}
        </div>
      )}
      {videos.map((m) => (
        <div key={m.id} className="relative">
          <video controls preload="metadata" className="rounded-xl mb-3 w-full max-h-64 bg-black">
            <source src={mediaUrl(m.id)} type={m.content_type} />
          </video>
          {editing && <RemoveBtn id={m.id} />}
        </div>
      ))}
      {editing ? (
        <div>
          <textarea data-testid={`live-edit-log-input-${e.id}`} value={draft} onChange={(ev) => setDraft(ev.target.value)} maxLength={2000} rows={3} autoFocus
            className="w-full bg-secondary rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#52B788] resize-none" />
          <label className="block text-[11px] text-muted-foreground mt-1">Datum & tijd van dit moment
            <input type="datetime-local" data-testid={`live-edit-log-ts-${e.id}`} value={draftTs} onChange={(ev) => setDraftTs(ev.target.value)}
              className="block w-full sm:w-auto mt-0.5 bg-secondary rounded-xl px-3 py-2 text-sm font-mono outline-none focus:ring-2 focus:ring-[#52B788]" />
          </label>
          <div className="flex gap-2 mt-1.5">
            <button data-testid={`live-edit-log-save-${e.id}`} onClick={saveEdit} disabled={saving}
              className="rounded-full bg-[#52B788] text-[#0F172A] text-xs font-bold px-4 py-1.5 flex items-center gap-1 disabled:opacity-50 active:scale-95 transition-[transform] duration-200">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Opslaan
            </button>
            <button data-testid={`live-edit-log-cancel-${e.id}`} onClick={() => setEditing(false)}
              className="rounded-full bg-secondary text-xs font-semibold px-4 py-1.5 flex items-center gap-1 hover:bg-secondary/70 transition-[background-color] duration-200">
              <X className="w-3 h-3" /> Annuleer
            </button>
          </div>
        </div>
      ) : (
        e.text && <p className="text-sm leading-relaxed">{e.text}{e.edited_at && <span className="text-[10px] text-muted-foreground ml-1.5">(bewerkt)</span>}</p>
      )}
      <div className="flex items-center justify-between mt-2">
        <p className="text-xs text-muted-foreground font-mono">
          {e.location_name && `${e.location_name} · `}{new Date(e.timestamp).toLocaleString("nl-BE", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
        </p>
        <span className="flex items-center gap-0.5">
          <button data-testid={`live-edit-log-${e.id}`} onClick={() => { setDraft(e.text || ""); setDraftTs(toLocalInput(e.timestamp)); setEditing(true); }} title="Bewerk"
            className="p-1.5 rounded-full hover:bg-secondary text-muted-foreground transition-[background-color] duration-200">
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button data-testid={`live-delete-log-${e.id}`} onClick={async () => { await api.delete(`/trips/${trip.id}/log/${e.id}`); onRefresh?.(); }} title="Verwijder"
            className="p-1.5 rounded-full hover:bg-red-500/10 text-red-400 transition-[background-color] duration-200">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </span>
      </div>
    </div>
  );
};

export const LiveMode = ({ trip, onExit, onRefresh }) => {
  const days = trip.days || [];
  const today = new Date().toISOString().slice(0, 10);
  const defaultIdx = Math.max(0, days.findIndex((d) => d.date >= today));
  const [dayIdx, setDayIdx] = useState(defaultIdx === -1 ? 0 : defaultIdx);
  const [logOpen, setLogOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [logText, setLogText] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [soc, setSoc] = useState("");
  const [replanning, setReplanning] = useState(false);
  const [daySoc, setDaySoc] = useState("");
  const [dayBusy, setDayBusy] = useState(false);
  const [livePos, setLivePos] = useState(null);
  const [liveKm, setLiveKm] = useState(null);
  const [stopStatuses, setStopStatuses] = useState(null);

  const day = days[dayIdx];
  const nextStop = day?.charging_stops?.[0];
  const firstStopName = nextStop?.name;

  const fallbackPos = useMemo(() => {
    const pts = (trip.live_track || []).filter((p) => p.lat && (!day?.date || String(p.ts || "").slice(0, 10) === day.date));
    return pts.length ? { lat: pts[pts.length - 1].lat, lon: pts[pts.length - 1].lon } : null;
  }, [trip.live_track, day?.date]);

  useEffect(() => {
    if (!day || day.type === "stay" || !(day.charging_stops || []).length) { setStopStatuses(null); return; }
    let alive = true;
    api.get(`/trips/${trip.id}/days/${day.day_number}/charging/status`)
      .then(({ data }) => {
        if (!alive) return;
        setStopStatuses(data);
        const bad = data.find((s) => s.stop_index === 0 && s.status === "INOPERATIVE");
        if (bad) toast.warning(`Let op: laadstop "${bad.name}" staat gemeld als buiten dienst`, { description: "Herplan met één tik via de rode knop bij je volgende laadstop.", duration: 10000 });
      })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trip.id, day?.day_number, firstStopName]);

  const stStatus = (i) => stopStatuses?.find((s) => s.stop_index === i)?.status;
  const STATUS_BADGE = { AVAILABLE: ["#52B788", "beschikbaar"], CHARGING: ["#F59E0B", "bezet"], INOPERATIVE: ["#F87171", "buiten dienst"] };

  const approachAlertedRef = useRef(null);
  useEffect(() => {
    if (!nextStop) return;
    let remain = null;
    if (liveKm != null && nextStop.km_from_day_start != null) remain = nextStop.km_from_day_start - liveKm;
    else if (livePos && nextStop.lat) remain = haversineKm(livePos, { lat: nextStop.lat, lon: nextStop.lon });
    if (remain == null || remain <= 0 || remain > 10) return;
    const key = `${day?.day_number}-${nextStop.name}`;
    if (approachAlertedRef.current === key) return;
    approachAlertedRef.current = key;
    const speed = day?.distance_km && day?.drive_time_hours ? day.distance_km / day.drive_time_hours : 90;
    const mins = Math.max(1, Math.round((remain / speed) * 60));
    if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
    toast.info(`⚡ Laadstop over ±${mins} min — ${nextStop.name}`, {
      description: `Nog ~${Math.round(remain)} km${nextStop.charge_minutes ? ` · ±${nextStop.charge_minutes} min laden gepland` : ""}${nextStop.max_power_kw ? ` · ${nextStop.max_power_kw} kW` : ""}`,
      duration: 20000,
    });
  }, [liveKm, livePos, nextStop, day?.day_number, day?.distance_km, day?.drive_time_hours]);

  const dayLogs = day ? (trip.log_entries || [])
    .filter((e) => (e.day_number != null ? e.day_number === day.day_number : (e.timestamp || "").slice(0, 10) === day.date))
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)) : [];

  const replan = async () => {
    if (!soc) return;
    setReplanning(true);
    try {
      const { data } = await api.post(`/trips/${trip.id}/days/${day.day_number}/replan`, {
        current_soc: parseFloat(soc),
        km_done_in_day: liveKm != null ? Math.round(liveKm) : 0,
      });
      toast.success(`Plan herrekend: ${data.charging_stops.length} laadstop${data.charging_stops.length === 1 ? "" : "s"} resterend, aankomst ~${data.arrival_soc}%`);
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setReplanning(false);
    }
  };

  const applyDaySoc = async () => {
    setDayBusy(true);
    try {
      const { data } = await api.patch(`/trips/${trip.id}/days/${day.day_number}/battery`, { start_soc: parseInt(daySoc) });
      toast.success(`Dag start met ${daySoc}% — ${data.charging_stops.length} laadstop${data.charging_stops.length === 1 ? "" : "s"}, aankomst ~${data.arrival_soc}%`);
      setDaySoc("");
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setDayBusy(false);
    }
  };

  const [nightBusy, setNightBusy] = useState(false);
  const addNightLive = async () => {
    const place = ((day.end?.name || "").split(",")[0]) || "deze plek";
    if (!(await confirmDialog(`Extra nacht in ${place} bijboeken? Je trip wordt 1 dag langer en de planning wordt opnieuw berekend.`, { title: "Extra nacht bijboeken", confirmLabel: "Bijboeken" }))) return;
    setNightBusy(true);
    try {
      await api.post(`/trips/${trip.id}/days/${day.day_number}/extra-night`);
      toast.success("Extra nacht bijgeboekt — je planning wordt opnieuw berekend");
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setNightBusy(false);
    }
  };

  const emergency = async () => {
    try {
      const { data } = await api.post(`/trips/${trip.id}/days/${day.day_number}/charging/0/emergency`);
      toast.success(`Nieuwe lader: ${data.name} (${data.max_power_kw || "?"} kW)`);
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const saveVoiceLog = async (blob) => {
    const fd = new FormData();
    fd.append("audio", blob, "log.webm");
    if (day?.end?.name) fd.append("location_name", day.end.name);
    const pos = isTripToday(trip) ? await getPositionIfGranted() : null;
    if (pos) { fd.append("lat", pos.lat); fd.append("lon", pos.lon); }
    try {
      await api.post(`/trips/${trip.id}/log/voice`, fd);
      toast.success("Ingesproken en door AI omgezet naar je reislog!");
      setLogOpen(false);
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const saveLog = async () => {
    if (!logText.trim()) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("text", logText);
      if (day?.end?.name) fd.append("location_name", day.end.name);
      if (photoFile) fd.append("photo", await compressImage(photoFile));
      const pos = isTripToday(trip) ? await getPositionIfGranted() : null;
      if (pos) { fd.append("lat", pos.lat); fd.append("lon", pos.lon); }
      await api.post(`/trips/${trip.id}/log`, fd);
      toast.success("Toegevoegd aan je reislog!");
      setLogText("");
      setPhotoFile(null);
      setLogOpen(false);
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!day) return null;

  return (
    <div className="dark fixed inset-0 z-50 bg-background text-foreground overflow-y-auto" data-testid="live-mode-view">
      <div className="max-w-lg mx-auto px-5 py-6 pb-24">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-[#52B788] animate-pulse-soft" />
            <span className="text-xs tracking-[0.15em] uppercase font-semibold text-[#52B788]">Live onderweg</span>
          </div>
          <button data-testid="live-mode-exit-button" onClick={onExit} className="p-3 rounded-full bg-card hover:bg-secondary transition-[background-color] duration-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex gap-2 overflow-x-auto hide-scrollbar mb-8">
          {days.map((d, i) => (
            <button key={d.day_number} data-testid={`live-day-tab-${d.day_number}`} onClick={() => setDayIdx(i)}
              className={`shrink-0 px-5 py-2.5 rounded-full text-sm font-semibold transition-[background-color,color] duration-200 ${i === dayIdx ? "bg-[#52B788] text-[#0F172A]" : "bg-card text-muted-foreground"}`}>
              Dag {d.day_number}
            </button>
          ))}
        </div>

        <h1 className="font-heading font-extrabold text-3xl tracking-tight mb-2">{day.start.name} → {day.end.name}</h1>
        <div className="flex gap-5 text-muted-foreground mb-6">
          <span className="flex items-center gap-1.5 text-sm"><Route className="w-4 h-4" /><span className="font-mono">{day.distance_km} km</span></span>
          <span className="flex items-center gap-1.5 text-sm"><Clock className="w-4 h-4" /><span className="font-mono">±{day.drive_time_hours}u</span></span>
          <span className="flex items-center gap-1.5 text-sm"><BatteryCharging className="w-4 h-4" /><span className="font-mono">~{day.arrival_soc}% bij aankomst</span></span>
        </div>

        <LiveMap trip={trip} day={day} onPosition={setLivePos} onRefresh={onRefresh} />

        {day.stay && (
          <div className="bg-card rounded-2xl p-4 mb-6 flex items-center gap-3" data-testid="live-stay-card">
            <span className="w-8 h-8 rounded-full bg-[#3A6EA5] flex items-center justify-center shrink-0">
              <BedDouble className="w-4 h-4 text-white" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground">Overnachting</p>
              <p className="text-sm font-semibold truncate">{day.stay.type === "eigen boeking" && <Lock data-testid="live-stay-lock" className="inline w-3.5 h-3.5 -mt-0.5 mr-1 text-emerald-600" />}{day.stay.name}</p>
            </div>
            <Button data-testid="live-extra-night" size="sm" variant="outline" disabled={nightBusy} onClick={addNightLive}
              className="rounded-full h-8 text-xs border-[#3A6EA5]/40 text-[#3A6EA5] hover:bg-[#3A6EA5]/10 shrink-0 transition-[background-color] duration-200">
              {nightBusy ? "Bezig..." : "+ Extra nacht"}
            </Button>
          </div>
        )}

        <LiveProgress trip={trip} day={day} pos={livePos} fallbackPos={fallbackPos} onKmDone={setLiveKm} onRefresh={onRefresh} />

        {trip.travel_mode !== "moto" && (
          <ChargeCalcDialog trip={trip} prefill={{ soc: soc || "" }}>
            <button data-testid="live-charge-calc-button"
              className="w-full bg-[#52B788]/15 border border-[#52B788]/30 rounded-2xl p-4 mb-6 flex items-center gap-3 text-left hover:bg-[#52B788]/25 active:scale-[0.99] transition-[background-color,transform] duration-200">
              <span className="w-9 h-9 rounded-full bg-[#52B788] flex items-center justify-center shrink-0">
                <Calculator className="w-4.5 h-4.5 text-[#0F172A]" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block font-heading font-bold text-sm">Laad-rekenaar</span>
                <span className="block text-xs text-muted-foreground">Bij de paal: kW invullen → km per uur & km na X minuten</span>
              </span>
              <Zap className="w-4 h-4 text-[#52B788] shrink-0" />
            </button>
          </ChargeCalcDialog>
        )}

        {day.type !== "stay" && (day.distance_km || 0) > 0 && (
          <div className="mb-2">
            <SocChart trip={trip} day={day}
              defaultSoc={day.day_number === (days.find((d) => d.type !== "stay" && (d.distance_km || 0) > 0) || {}).day_number ? (trip.start_battery_pct || 100) : 100}
              liveKm={day.date === today && liveKm != null ? liveKm : null} />
          </div>
        )}

        {day.type !== "stay" && (
          <div className="bg-card rounded-2xl p-5 mb-6" data-testid="soc-replan-card">
            <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-3 flex items-center gap-2">
              <BatteryCharging className="w-4 h-4 text-[#52B788]" /> Batterij-check & herplanning
            </p>
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <label className="text-xs text-muted-foreground block mb-1">Batterij nu (%)</label>
                <input data-testid="soc-input" type="number" min="1" max="100" value={soc} onChange={(e) => setSoc(e.target.value)} placeholder="bv. 45"
                  className="w-full bg-secondary rounded-xl px-4 py-3 text-sm font-mono outline-none focus:ring-2 focus:ring-[#52B788] transition-[box-shadow] duration-200" />
              </div>
              <Button data-testid="replan-button" onClick={replan} disabled={replanning || !soc}
                className="rounded-full bg-[#52B788] hover:bg-[#74C69D] text-[#0F172A] font-bold h-11 px-5 active:scale-95 transition-[transform,background-color] duration-200">
                <RefreshCw className={`w-4 h-4 ${replanning ? "animate-spin" : ""}`} />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Voer je huidige batterij% in — de resterende laadstops worden herrekend
              {liveKm != null ? ` vanaf je live positie (${Math.round(liveKm)} km gereden vandaag).` : " voor de rest van vandaag."}
            </p>
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/10">
              <span className="text-xs text-muted-foreground shrink-0">Vertrokken met</span>
              <input data-testid="live-day-soc-input" type="number" min="5" max="100" value={daySoc}
                onChange={(e) => setDaySoc(e.target.value)} placeholder={String(day.start_soc_override ?? 100)}
                className="w-16 bg-secondary rounded-lg px-2 py-1.5 text-sm font-mono outline-none focus:ring-2 focus:ring-[#52B788] transition-[box-shadow] duration-200" />
              <span className="text-xs text-muted-foreground">% vandaag</span>
              <Button data-testid="live-day-soc-apply" size="sm" variant="outline" onClick={applyDaySoc}
                disabled={dayBusy || !daySoc}
                className="rounded-full h-8 text-xs ml-auto border-[#52B788]/40 text-[#52B788] hover:bg-[#52B788]/10 transition-[background-color] duration-200">
                {dayBusy ? "Bezig..." : "Herbereken dag"}
              </Button>
            </div>
          </div>
        )}

        {nextStop ? (
          <div className="bg-card rounded-2xl p-6 mb-6 border border-[#52B788]/30">
            <p className="text-xs tracking-[0.15em] uppercase font-semibold text-[#52B788] mb-3">Volgende laadstop</p>
            {stStatus(0) === "INOPERATIVE" && (
              <div data-testid="next-stop-inoperative-warning" className="mb-4 rounded-xl bg-red-500/15 border border-red-400/40 px-4 py-3 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                <p className="text-xs text-red-300 font-semibold leading-relaxed">Deze laadstop staat gemeld als <strong>buiten dienst</strong> — herplan hieronder met één tik naar een alternatief in de buurt.</p>
              </div>
            )}
            <div className="flex items-start gap-4">
              <span className="w-12 h-12 rounded-full bg-[#52B788] flex items-center justify-center shrink-0">
                <Zap className="w-6 h-6 text-[#0F172A]" />
              </span>
              <div>
                <p className="font-heading font-bold text-xl">
                  {nextStop.name}
                  {STATUS_BADGE[stStatus(0)] && (
                    <span data-testid="next-stop-status-badge" className="ml-2 inline-flex items-center gap-1 align-middle text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5"
                      style={{ background: `${STATUS_BADGE[stStatus(0)][0]}22`, color: STATUS_BADGE[stStatus(0)][0] }}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: STATUS_BADGE[stStatus(0)][0] }} />
                      {STATUS_BADGE[stStatus(0)][1]}
                    </span>
                  )}
                </p>
                <p className="text-sm text-muted-foreground">{nextStop.town} {nextStop.operator ? `· ${nextStop.operator}` : ""}</p>
                <p className="font-mono text-sm mt-2 text-[#52B788]">
                  {nextStop.max_power_kw ? `${nextStop.max_power_kw} kW` : ""} · {nextStop.arrival_soc}% → 80% · ±{nextStop.charge_minutes} min
                </p>
                {nextStop.cost_eur != null && <p className="font-mono text-xs mt-1 text-muted-foreground">~{nextStop.energy_kwh} kWh · €{nextStop.cost_eur.toFixed(2)}</p>}
                {nextStop.km_from_day_start != null && (
                  <p className="font-mono text-xs mt-1.5 text-[#52B788]" data-testid="next-stop-distance">
                    na {Math.round(nextStop.km_from_day_start)} km rijden volgens planning
                    {liveKm != null && ` · nog ${Math.max(0, Math.round(nextStop.km_from_day_start - liveKm))} km (live)`}
                  </p>
                )}
              </div>
            </div>
            <ChargeTimer trip={trip} day={day} stop={nextStop} stopIndex={0} />
            <button data-testid="live-emergency-button" onClick={emergency}
              className="mt-4 w-full rounded-full border border-red-400/40 text-red-400 hover:bg-red-400/10 text-sm font-semibold py-2.5 flex items-center justify-center gap-2 transition-[background-color] duration-200">
              <AlertTriangle className="w-4 h-4" /> Lader bezet of defect? Herplan met één tik
            </button>
          </div>
        ) : (
          <div className="bg-card rounded-2xl p-6 mb-6">
            <p className="text-sm text-muted-foreground">Geen laadstop nodig vandaag — je batterij is voldoende voor deze etappe. 🎉</p>
          </div>
        )}

        {day.charging_stops?.length > 1 && (
          <div className="space-y-3 mb-6">
            {day.charging_stops.slice(1).map((cs, i) => (
              <div key={`${cs.name}-${i}`} className="bg-card rounded-2xl p-4 flex items-center gap-3">
                <Zap className="w-4 h-4 text-[#52B788] shrink-0" />
                <div>
                  <p className="font-semibold text-sm">
                    {cs.name}
                    {STATUS_BADGE[stStatus(i + 1)] && (
                      <span className="ml-2 inline-flex items-center gap-1 align-middle text-[9px] font-bold uppercase tracking-wide rounded-full px-1.5 py-0.5"
                        style={{ background: `${STATUS_BADGE[stStatus(i + 1)][0]}22`, color: STATUS_BADGE[stStatus(i + 1)][0] }}>
                        {STATUS_BADGE[stStatus(i + 1)][1]}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground font-mono">
                    {cs.max_power_kw ? `${cs.max_power_kw} kW · ` : ""}±{cs.charge_minutes} min
                    {cs.km_from_day_start != null && ` · na ${Math.round(cs.km_from_day_start)} km${liveKm != null ? ` (nog ${Math.max(0, Math.round(cs.km_from_day_start - liveKm))} km)` : ""}`}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {day.stay && (
          <div className="bg-card rounded-2xl p-5 mb-6 flex items-start gap-3">
            <MapPin className="w-5 h-5 text-[#3A6EA5] shrink-0 mt-0.5" />
            <div>
              <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-1">Vanavond</p>
              <p className="font-semibold">{day.stay.name} <span className="text-muted-foreground text-sm">({day.stay.type})</span>
                {day.stay.type === "eigen boeking" && (
                  <span data-testid="live-tonight-lock" title="Eigen boeking — dit verblijf staat definitief vast"
                    className="inline-flex items-center gap-1 ml-1.5 align-middle text-[10px] font-bold text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5">
                    <Lock className="w-3 h-3" /> bevestigd
                  </span>
                )}
              </p>
              {day.stay.type !== "eigen boeking" && (
                <div className="flex gap-2 mt-2.5">
                  <a data-testid={`live-booking-link-${day.day_number}`} href={stayLinks(trip, day).booking} target="_blank" rel="noreferrer"
                    className="text-[11px] font-bold bg-[#003580] text-white rounded-full px-3 py-1.5 hover:opacity-85 transition-opacity duration-200">Booking.com</a>
                  <a data-testid={`live-airbnb-link-${day.day_number}`} href={stayLinks(trip, day).airbnb} target="_blank" rel="noreferrer"
                    className="text-[11px] font-bold bg-[#FF385C] text-white rounded-full px-3 py-1.5 hover:opacity-85 transition-opacity duration-200">Airbnb</a>
                </div>
              )}
            </div>
          </div>
        )}

        {(trip.log_entries || []).length > 0 && (
          <div className="mb-6">
            <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-3 flex items-center gap-2">
              <BookOpen className="w-4 h-4" /> Reislog — dag {day.day_number}
            </p>
            {dayLogs.length > 0 ? (
              <div className="space-y-3" data-testid="live-log-day-list">
                {dayLogs.map((e) => (
                  <LiveLogEntry key={e.id} trip={trip} e={e} onRefresh={onRefresh} />
                ))}
              </div>
            ) : (
              <div className="bg-card rounded-2xl p-4" data-testid="live-log-empty">
                <p className="text-xs text-muted-foreground">Nog geen momenten op dag {day.day_number}. Momenten van andere dagen bekijk je via de dag-tabs bovenaan.</p>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="fixed bottom-0 inset-x-0 glass-panel border-t border-border/40 p-4">
        <div className="max-w-lg mx-auto flex gap-3">
          <button data-testid="live-to-planning-button" onClick={onExit} title="Terug naar planning"
            className="shrink-0 rounded-full bg-card border border-border text-foreground font-bold h-14 px-4 text-sm flex flex-col items-center justify-center leading-tight active:scale-95 transition-[transform,background-color] duration-200 hover:bg-secondary">
            <CalendarDays className="w-5 h-5" />
            <span className="text-[10px] font-semibold">Planning</span>
          </button>
          <Button data-testid="live-add-log-button" onClick={() => setLogOpen(true)}
            className="flex-1 rounded-full bg-[#52B788] hover:bg-[#74C69D] text-[#0F172A] font-bold h-14 text-base active:scale-95 transition-[transform,background-color] duration-200">
            <Plus className="w-5 h-5 mr-2" /> Reislog
          </Button>
          {day.type !== "stay" && (
            <a data-testid="live-navigate-button" href={gmapsUrl(trip, day, true)} target="_blank" rel="noreferrer"
              className="flex-1 rounded-full bg-card border border-border text-foreground font-bold h-14 text-base flex items-center justify-center gap-2 active:scale-95 transition-[transform,background-color] duration-200 hover:bg-secondary">
              <Navigation className="w-5 h-5" /> Navigeer
            </a>
          )}
        </div>
      </div>

      {assistantOpen && (
        <div data-testid="live-assistant-panel" className="fixed bottom-40 right-4 z-[60] w-[min(400px,calc(100vw-2rem))] rounded-2xl shadow-2xl border border-border overflow-hidden">
          <AssistantChat tripId={trip.id} canApply={trip.is_owner !== false} onApplied={onRefresh} liveDay={day.day_number} />
        </div>
      )}
      <button data-testid="live-assistant-bubble" onClick={() => setAssistantOpen((o) => !o)} title="AI-copiloot"
        className={`fixed bottom-24 right-4 z-[60] w-14 h-14 rounded-full shadow-[0_8px_30px_rgba(82,183,136,0.45)] flex items-center justify-center hover:scale-110 active:scale-95 transition-[transform] duration-200 ${assistantOpen ? "bg-[#52B788] text-[#0F172A]" : "bg-white vr-avatar-bob"}`}>
        {assistantOpen ? <X className="w-6 h-6" /> : <img src="/volt-avatar.png" alt="AI-copiloot" className="w-14 h-14 rounded-full" />}
      </button>

      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent className="dark bg-card text-foreground rounded-2xl max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Nieuw logbericht</DialogTitle></DialogHeader>
          <Textarea data-testid="live-log-textarea" value={logText} onChange={(e) => setLogText(e.target.value)}
            placeholder="Wat heb je beleefd? bv. 'Prachtige laadstop met uitzicht op de Vogezen ⛰️'" rows={4} className="rounded-xl" />
          <label data-testid="live-log-photo-label" className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer bg-secondary rounded-xl px-4 py-3 hover:bg-secondary/70 transition-[background-color] duration-200">
            <ImagePlus className="w-4 h-4 shrink-0" />
            <span className="truncate">{photoFile ? photoFile.name : "Foto toevoegen (optioneel)"}</span>
            <input data-testid="live-log-photo-input" type="file" accept="image/*" className="hidden" onChange={(e) => setPhotoFile(e.target.files?.[0] || null)} />
          </label>
          <Button data-testid="live-log-save-button" onClick={saveLog} disabled={saving || !logText.trim()}
            className="rounded-full bg-[#52B788] hover:bg-[#74C69D] text-[#0F172A] font-bold">
            {saving ? "Opslaan..." : "Opslaan in reislog"}
          </Button>
          <div className="border-t border-border/40 pt-3">
            <p className="text-xs text-muted-foreground mb-2">Of spreek je moment in — AI maakt er een leesbaar logbericht van:</p>
            <VoiceRecorder onRecorded={saveVoiceLog} label="Spreek je reislog in" />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
